import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coffee, Circle, UserX, Power } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  {
    value: 'available',
    label: "I'M AVAILABLE",
    shortLabel: 'Available',
    icon: Circle,
    // bar styling
    bar: 'bg-gradient-to-r from-emerald-600 to-emerald-500 border-emerald-400',
    dotBg: 'bg-emerald-400',
    pulse: 'bg-emerald-300',
    // button styling when NOT selected
    btnIdle: 'bg-white/10 text-white/80 hover:bg-white/20',
    btnActive: 'bg-white text-emerald-700 shadow-md',
  },
  {
    value: 'on_break',
    label: "I'M ON BREAK",
    shortLabel: 'On Break',
    icon: Coffee,
    bar: 'bg-gradient-to-r from-amber-600 to-amber-500 border-amber-400',
    dotBg: 'bg-amber-300',
    pulse: 'bg-amber-200',
    btnIdle: 'bg-white/10 text-white/80 hover:bg-white/20',
    btnActive: 'bg-white text-amber-700 shadow-md',
  },
  {
    value: 'away',
    label: "I'M AWAY",
    shortLabel: 'Away',
    icon: UserX,
    bar: 'bg-gradient-to-r from-rose-600 to-rose-500 border-rose-400',
    dotBg: 'bg-rose-300',
    pulse: 'bg-rose-200',
    btnIdle: 'bg-white/10 text-white/80 hover:bg-white/20',
    btnActive: 'bg-white text-rose-700 shadow-md',
  },
  {
    value: 'offline',
    label: "I'M OFF DUTY",
    shortLabel: 'Off Duty',
    icon: Power,
    bar: 'bg-gradient-to-r from-slate-700 to-slate-600 border-slate-400',
    dotBg: 'bg-slate-300',
    pulse: 'bg-slate-200',
    btnIdle: 'bg-white/10 text-white/80 hover:bg-white/20',
    btnActive: 'bg-white text-slate-800 shadow-md',
  },
];

/**
 * Full-width prominent status banner for clerks that sits at the top of every page.
 * Shows a large pulsing "light" in the status colour + the wording in bold,
 * and exposes all 4 status options as inline buttons for a one-click toggle.
 * Every change fires email notifications to the configured admin addresses.
 */
export default function ClerkStatusBanner() {
  const [status, setStatus] = useState('available');
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clerks/my-status`, { headers });
      setStatus(res.data.status || 'available');
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleChange = async (value) => {
    if (value === status || updating) return;
    setUpdating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/clerks/my-status`,
        { status: value },
        { headers }
      );
      setStatus(res.data.status);
      toast.success(`Status set to ${res.data.label} — admin notified by email`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const current = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  const Icon = current.icon;

  return (
    <div
      className={`w-full rounded-xl border-2 ${current.bar} text-white shadow-lg px-4 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-6 transition-colors duration-300`}
      data-testid="clerk-status-banner"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        {/* Big pulsing light + wording */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="relative flex-shrink-0">
            {status === 'available' && (
              <span className={`absolute inset-0 rounded-full ${current.pulse} opacity-75 animate-ping`} />
            )}
            <span className={`relative block h-5 w-5 sm:h-6 sm:w-6 rounded-full ${current.dotBg} ring-4 ring-white/30 shadow-[0_0_24px_rgba(255,255,255,0.55)]`} />
          </div>
          <div className="flex items-center gap-3 min-w-0">
            <Icon className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0" fill={status === 'available' ? 'currentColor' : 'none'} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider opacity-80 leading-none mb-1">My Status</div>
              <div className="text-xl sm:text-2xl font-extrabold leading-tight truncate" data-testid="clerk-status-label">
                {current.label}
              </div>
            </div>
          </div>
        </div>

        {/* Toggle buttons */}
        <div className="grid grid-cols-4 gap-1.5 w-full md:w-auto">
          {STATUS_OPTIONS.map(opt => {
            const OptIcon = opt.icon;
            const isActive = opt.value === status;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={updating}
                onClick={() => handleChange(opt.value)}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${isActive ? opt.btnActive : opt.btnIdle} ${updating ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={`status-btn-${opt.value}`}
                title={opt.shortLabel}
              >
                <OptIcon className="h-3.5 w-3.5" />
                <span className="whitespace-nowrap">{opt.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
